import { createContext, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  STOREFRONT_DEFAULT,
  getSiteManifest,
  resolveBrand,
  resolveStorefront,
  type StorefrontConfig,
  type StorefrontPage,
  type StorefrontPageSection,
} from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  storefrontIcon,
} from '@mlm/ui';
import {
  getTenantFeatures,
  listProducts,
  patchTenantFeatures,
  storefrontAssetUrl,
  uploadStorefrontAsset,
  type Product,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { RepeatableRows, type RowField } from '../components/repeatable-rows';
import { SitePagesEditor } from '../components/storefront/site-pages-editor';
import { TenantLogosCard } from '../components/logo-upload-field';

/** Bespoke-site content manifest for THIS build's tenant (baked VITE_TENANT).
 * Undefined on baseline/theme-only tenants — the Site Pages tab is hidden. */
const SITE_MANIFEST = getSiteManifest(import.meta.env.VITE_TENANT as string | undefined);

/** Editor tabs the tenant's manifest declares unused (its bespoke pages never
 * render those shared surfaces). Cosmetic — the config stays in the DB. */
const HIDDEN_TABS = new Set(SITE_MANIFEST?.adminHide?.storefrontTabs ?? []);
const showTab = (id: string) => !HIDDEN_TABS.has(id);

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** Background for a banner preview: uploaded image → CSS gradient → fallback.
 * Mirrors the storefront's own `bannerStyle()` so the preview matches the page. */
function bannerBg(
  row: { imageKey?: unknown; gradient?: unknown },
  // Mirrors the storefront's `bannerStyle`: a full-image banner (no caption
  // text) is shown undimmed, so the preview matches what visitors get.
  dim = true,
): React.CSSProperties {
  const imageKey = typeof row.imageKey === 'string' ? row.imageKey : '';
  const gradient = typeof row.gradient === 'string' ? row.gradient : '';
  if (imageKey) {
    const url = `url("${storefrontAssetUrl(imageKey)}")`;
    const wash = 'linear-gradient(rgba(0,0,0,.28),rgba(0,0,0,.28))';
    return {
      backgroundImage: dim ? `${wash}, ${url}` : url,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return { background: gradient || 'linear-gradient(120deg,#FF6633,#EE4D2D 55%,#D4380D)' };
}

/** True when any caption field carries real text — mirrors `hasBannerText`. */
function anyText(row: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((k) => typeof row[k] === 'string' && (row[k] as string).trim() !== '');
}

/** A banner preview card (bottom + campaign banners) drawn with the row's
 * actual gradient/image and text, so the admin sees the rendered result. */
function BannerPreview({
  row,
  cta,
}: {
  row: { title?: unknown; sub?: unknown; ctaLabel?: unknown; imageKey?: unknown; gradient?: unknown };
  cta?: boolean;
}) {
  const title = typeof row.title === 'string' ? row.title : '';
  const sub = typeof row.sub === 'string' ? row.sub : '';
  const ctaLabel = typeof row.ctaLabel === 'string' ? row.ctaLabel : '';
  const captioned = anyText(row, cta ? ['title', 'sub', 'ctaLabel'] : ['title', 'sub']);
  const hasImage = typeof row.imageKey === 'string' && row.imageKey !== '';

  // Same rule as the hero: all-blank ⇒ full-image banner, undimmed and with
  // nothing drawn over it.
  if (!captioned) {
    return (
      <div className="flex min-h-16 items-end justify-end rounded-md p-2" style={bannerBg(row, false)}>
        <span className="rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {hasImage ? 'Full image — no text overlay' : 'Add an image or some text'}
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-16 flex-col justify-center gap-0.5 rounded-md px-4 py-3 text-white"
      style={bannerBg(row)}
    >
      {title && <div className="text-sm font-bold drop-shadow">{title}</div>}
      {sub && <div className="text-xs opacity-90 drop-shadow">{sub}</div>}
      {cta && ctaLabel && (
        <span className="mt-1 self-start rounded bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-[#EE4D2D]">
          {ctaLabel}
        </span>
      )}
    </div>
  );
}

/** A hero-slide preview mock (kicker / title / highlight / date over the bg). */
function HeroPreview({ row }: { row: Record<string, unknown> }) {
  const s = (k: string) => (typeof row[k] === 'string' ? (row[k] as string) : '');
  const captioned = anyText(row, ['kicker', 'title', 'highlight', 'date']);
  const hasImage = typeof row.imageKey === 'string' && row.imageKey !== '';

  // Leave every field blank and the slide is a FULL-IMAGE banner: the live page
  // draws no text and applies no dark wash. Showing placeholder copy here would
  // promise an overlay the storefront won't render.
  if (!captioned) {
    return (
      <div
        className="flex min-h-20 items-end justify-end rounded-md p-2"
        style={bannerBg({ imageKey: row.imageKey }, false)}
      >
        <span className="rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {hasImage ? 'Full image — no text overlay' : 'Add an image or some text'}
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-20 flex-col justify-center gap-0.5 rounded-md px-4 py-3 text-white"
      style={bannerBg({ imageKey: row.imageKey })}
    >
      {s('kicker') && (
        <div className="text-[11px] font-semibold uppercase tracking-wide opacity-90 drop-shadow">
          {s('kicker')}
        </div>
      )}
      {s('title') && (
        <div className="text-base font-extrabold leading-tight drop-shadow">{s('title')}</div>
      )}
      {s('highlight') && (
        <div className="text-sm font-bold text-[#FFE08A] drop-shadow">{s('highlight')}</div>
      )}
      {s('date') && <div className="text-[11px] opacity-90 drop-shadow">{s('date')}</div>}
    </div>
  );
}

/** An icon-badge / icon-stat preview: the actual glyph next to label (+ value). */
function IconRowPreview({ row }: { row: Record<string, unknown> }) {
  const key = typeof row.icon === 'string' ? row.icon : 'box';
  const Icon = storefrontIcon(key as never);
  const label = typeof row.label === 'string' ? row.label : '';
  const value = typeof row.value === 'string' ? row.value : '';
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <Icon className="h-4 w-4 text-foreground" />
      {value && <b className="tabular-nums">{value}</b>}
      <span className="text-muted-foreground">{label || 'Label'}</span>
    </div>
  );
}

/**
 * Storefront CMS — edits the buyer storefront's marketing chrome (hero, banners,
 * vouchers, collections, store stats, badges, header/footer, copy). Persisted as
 * the full `storefront` block on tenant_config.features (super_admin write). All
 * editing is through typed row editors — no raw JSON. Six tabs group the surfaces.
 */
export function StorefrontPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('tenant.storefront.write');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [draft, setDraft] = useState<StorefrontConfig | null>(null);

  useEffect(() => {
    if (q.data && !draft) {
      // resolveStorefront fills the (newer) brand block from defaults even if an
      // older API payload omitted it, so the Brand tab always has a full block.
      setDraft(clone(resolveStorefront(q.data.storefront ?? STOREFRONT_DEFAULT)));
    }
  }, [q.data, draft]);

  const save = useMutation({
    meta: { successMessage: 'Storefront saved.' },
    mutationFn: (cfg: StorefrontConfig) => patchTenantFeatures({ storefront: cfg }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-features'] });
      // Also refresh the public storefront-content query so any in-app preview
      // reflects the save immediately (the buyer SPA refetches on its own).
      qc.invalidateQueries({ queryKey: ['storefront-content'] });
    },
  });

  // Active tab + the section the admin is hovering/editing — drives the "Where
  // it appears" locator pane so they can see which part of the page a setting
  // controls. Switching tabs highlights that tab's first section by default.
  const [tab, setTab] = useState('brand');
  const [activeSection, setActiveSection] = useState<string | null>('brand-identity');

  if (q.isLoading || !draft) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  // Patch a top-level key on the draft.
  const set = <K extends keyof StorefrontConfig>(key: K, value: StorefrontConfig[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const setBrand = <K extends keyof StorefrontConfig['brand']>(
    key: K,
    value: StorefrontConfig['brand'][K],
  ) => set('brand', { ...draft.brand, [key]: value });

  // Live-derived brand terms — drive the "empty = auto" placeholders so an admin
  // sees exactly what an unset field will render (recomputes as they type a name).
  const derivedBrand = resolveBrand(draft);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Storefront</h1>
        <ConfirmSaveButton
          label="Save"
          title="Save storefront content"
          current={q.data?.storefront}
          next={draft}
          loading={save.isPending}
          disabled={!canWrite}
          onConfirm={() => save.mutateAsync(draft)}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Controls the buyer storefront's marketing content — hero, banners, vouchers, collections,
        store stats, badges, header &amp; footer, and copy. Empty sections are hidden on the
        storefront. {canWrite ? '' : 'Read-only — requires the storefront-content permission.'}
      </p>
      {save.isSuccess && !save.isPending && <p className="text-sm text-success">Saved.</p>}
      {save.error && <p className="text-sm text-destructive">Save failed. Try again.</p>}

      <SetActiveSectionContext.Provider value={setActiveSection}>
        <div className="flex items-start gap-6">
          <div className="min-w-0 flex-1">
            <Tabs
              value={tab}
              onValueChange={(t) => {
                setTab(t);
                setActiveSection(TAB_REGIONS[t]?.[0]?.id ?? null);
              }}
            >
              <TabsList className="flex-wrap">
                <TabsTrigger value="brand">Brand</TabsTrigger>
                {showTab('home') && <TabsTrigger value="home">Home</TabsTrigger>}
                {showTab('official') && <TabsTrigger value="official">Official Store</TabsTrigger>}
                {showTab('shop') && <TabsTrigger value="shop">Shop Page</TabsTrigger>}
                {showTab('product') && <TabsTrigger value="product">Product Page</TabsTrigger>}
                {showTab('chrome') && <TabsTrigger value="chrome">Header &amp; Footer</TabsTrigger>}
                {showTab('pages') && <TabsTrigger value="pages">Pages</TabsTrigger>}
                {SITE_MANIFEST && <TabsTrigger value="sitePages">Site Pages</TabsTrigger>}
                {showTab('help') && <TabsTrigger value="help">Help &amp; Social</TabsTrigger>}
                {showTab('general') && <TabsTrigger value="general">General</TabsTrigger>}
              </TabsList>

        {/* ── Brand ── */}
        <TabsContent value="brand" className="space-y-6 pt-4">
          <Section
            id="brand-identity"
            title="Brand identity"
            hint="What the storefront calls itself everywhere. Leave a field empty to auto-derive it."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="Brand name">
                <Input
                  value={draft.brand.brandName}
                  placeholder={`Auto — “${derivedBrand.brandName}” (from the tenant name)`}
                  onChange={(e) => setBrand('brandName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Company / legal name">
                <Input
                  value={draft.brand.companyName}
                  placeholder={`Auto — “${derivedBrand.companyName}”`}
                  onChange={(e) => setBrand('companyName', e.target.value)}
                />
              </Labeled>
            </div>
            <Labeled label="Browser tab title">
              <Input
                value={draft.brand.siteTitle}
                placeholder={`Auto — “${derivedBrand.siteTitle}” (from the brand name)`}
                onChange={(e) => setBrand('siteTitle', e.target.value)}
              />
            </Labeled>
            <p className="mt-2 text-xs text-muted-foreground">
              The company name is used for the copyright line and “Sold &amp; shipped by”. The tab
              title is the only place to put a full name plus a tagline — keep the brand name itself
              short, since it also renders the logo, the mall badge and the wallet label.
            </p>
          </Section>
          <Section
            id="brand-terms"
            title="Storefront terms"
            hint="Names shown across the storefront. Empty = auto-derived from the brand name (see each placeholder)."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="House store name">
                <Input
                  value={draft.brand.houseStoreName}
                  placeholder={`Auto — “${derivedBrand.houseStoreName}”`}
                  onChange={(e) => setBrand('houseStoreName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Marketplace seller name">
                <Input
                  value={draft.brand.marketplaceSellerName}
                  placeholder={`Auto — “${derivedBrand.marketplaceSellerName}”`}
                  onChange={(e) => setBrand('marketplaceSellerName', e.target.value)}
                />
              </Labeled>
              <Labeled label="Mall badge text">
                <Input
                  value={draft.brand.mallBadgeText}
                  placeholder={`Auto — “${derivedBrand.mallBadgeText}”`}
                  onChange={(e) => setBrand('mallBadgeText', e.target.value)}
                />
              </Labeled>
              <Labeled label="Wallet label">
                <Input
                  value={draft.brand.walletLabel}
                  placeholder={`Auto — “${derivedBrand.walletLabel}”`}
                  onChange={(e) => setBrand('walletLabel', e.target.value)}
                />
              </Labeled>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.brand.showMallBadge}
                onCheckedChange={(v) => setBrand('showMallBadge', v === true)}
              />
              <span className="text-muted-foreground">Show the mall badge on product cards</span>
            </label>
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: the tokens <code className="font-mono">{'{brand}'}</code>,{' '}
              <code className="font-mono">{'{company}'}</code>,{' '}
              <code className="font-mono">{'{wallet}'}</code> and{' '}
              <code className="font-mono">{'{year}'}</code> work inside any storefront text field and
              expand to the values above.
            </p>
          </Section>
          <Section
            id="brand-logos"
            title="Logos"
            hint="The brand logo, a dark-surface variant, and the favicon."
          >
            <TenantLogosCard disabled={!canWrite} />
          </Section>
        </TabsContent>

        {/* ── Home ── */}
        <TabsContent value="home" className="space-y-6 pt-4">
          <Section
            id="hero"
            title="Hero slides"
            hint="The rotating banner at the top of the home page. Every text field is optional — leave them all blank and the slide shows your image on its own, with no text and no darkening over it."
          >
            <RepeatableRows
              items={draft.heroSlides}
              onChange={(v) => set('heroSlides', v)}
              addLabel="Add slide"
              makeBlank={() => ({ kicker: '', title: '', highlight: '', date: '' })}
              fields={[
                { kind: 'text', key: 'kicker', label: 'Kicker', placeholder: '6.6 Super Sale' },
                { kind: 'text', key: 'title', label: 'Title', placeholder: 'Campaign Vouchers' },
                { kind: 'text', key: 'highlight', label: 'Highlight', placeholder: 'up to ₱3,000 OFF' },
                { kind: 'text', key: 'date', label: 'Date / tagline', placeholder: 'June 1 – 8' },
                { kind: 'image', key: 'imageKey', label: 'Background image (optional)' },
              ]}
              preview={(row) => <HeroPreview row={row} />}
            />
          </Section>
          <Section id="home-side-banners" title="Hero side banners" hint="The two stacked tiles beside the hero. Upload an image for each — text is optional, and a tile with none shows the image alone, undarkened.">
            <RepeatableRows
              items={draft.homeSideBanners}
              onChange={(v) => set('homeSideBanners', v)}
              addLabel="Add tile"
              makeBlank={() => ({ title: '', sub: '', gradient: '' })}
              fields={[
                { kind: 'image', key: 'imageKey', label: 'Image' },
                { kind: 'text', key: 'title', label: 'Title (optional)' },
                { kind: 'text', key: 'sub', label: 'Subtitle (optional)' },
                { kind: 'gradient', key: 'gradient', label: 'Background gradient (when no image)' },
              ]}
              preview={(row) => <BannerPreview row={row} />}
            />
          </Section>
          <Section id="home-banners" title="Bottom banners" hint="Promo cards below the product grid. Text is optional — leave it blank for a full-image banner (no overlay, no darkening).">
            <RepeatableRows
              items={draft.bottomBanners}
              onChange={(v) => set('bottomBanners', v)}
              addLabel="Add banner"
              makeBlank={() => ({ title: '', sub: '', gradient: '' })}
              fields={[
                { kind: 'text', key: 'title', label: 'Title' },
                { kind: 'text', key: 'sub', label: 'Subtitle' },
                { kind: 'gradient', key: 'gradient', label: 'Background gradient (when no image)' },
                { kind: 'image', key: 'imageKey', label: 'Image (optional)' },
              ]}
              preview={(row) => <BannerPreview row={row} />}
            />
          </Section>
        </TabsContent>

        {/* ── Official Store ── */}
        <TabsContent value="official" className="space-y-6 pt-4">
          <Section id="store-stats" title="Store stats" hint="The stat bar in the official-store header (Products / Followers / Ratings) is now computed live from real data — there's nothing to edit here.">
            <p className="text-sm text-muted-foreground">
              Products, Followers, and Ratings show live values automatically.
            </p>
          </Section>
          <Section id="collections" title="Shop by Category" hint="The official-store category grid is now built from your real product categories — each tile shows its live product count.">
            <p className="text-sm text-muted-foreground">
              Manage the categories and their tile images under{' '}
              <strong>Products → Categories</strong>. Item counts are computed live.
            </p>
          </Section>
          <Section id="member-deals" title="Member-Exclusive Deals" hint="Pick the products shown in the official-store 'Member-Exclusive Deals' row, in order. Empty = the row is hidden.">
            <MemberExclusiveDealsEditor
              selectedIds={draft.memberExclusiveDealProductIds}
              onChange={(v) => set('memberExclusiveDealProductIds', v)}
            />
          </Section>
          <Section id="campaign" title="Campaign banners" hint="The two large promo banners with a call-to-action. Text and button are optional — leave them blank for a full-image banner (no overlay, no darkening).">
            <RepeatableRows
              items={draft.campaignBanners}
              onChange={(v) => set('campaignBanners', v)}
              addLabel="Add campaign"
              makeBlank={() => ({ title: '', sub: '', ctaLabel: '', ctaHref: '/', gradient: '' })}
              fields={[
                { kind: 'text', key: 'title', label: 'Title' },
                { kind: 'text', key: 'sub', label: 'Subtitle' },
                { kind: 'text', key: 'ctaLabel', label: 'Button label' },
                { kind: 'text', key: 'ctaHref', label: 'Button link', mono: true },
                { kind: 'gradient', key: 'gradient', label: 'Background gradient (when no image)' },
                { kind: 'image', key: 'imageKey', label: 'Image (optional)' },
              ]}
              preview={(row) => <BannerPreview row={row} cta />}
            />
          </Section>
        </TabsContent>

        {/* ── Shop Page ── */}
        <TabsContent value="shop" className="space-y-6 pt-4">
          <Section id="vouchers" title="Vouchers" hint="The claimable voucher strip on the shop page (display-only promo cards).">
            <RepeatableRows
              items={draft.vouchers}
              onChange={(v) => set('vouchers', v)}
              addLabel="Add voucher"
              makeBlank={() => ({ big: '', sm: 'OFF', terms: '', validity: '' })}
              fields={[
                { kind: 'text', key: 'big', label: 'Big value (e.g. ₱100)' },
                { kind: 'text', key: 'sm', label: 'Small (e.g. OFF)' },
                { kind: 'text', key: 'terms', label: 'Terms (e.g. Min. spend ₱1,000)' },
                { kind: 'text', key: 'validity', label: 'Validity (e.g. Valid till 06.30)' },
              ]}
            />
          </Section>
          <Section id="shop-stats" title="Shop header stats" hint="The shop page stat row (Products / Rating / Transactions / Followers) is now computed live from real data — there's nothing to edit here.">
            <p className="text-sm text-muted-foreground">
              Merchant shops show that merchant's live stats; the house shop shows the Official Store's
              live stats automatically.
            </p>
          </Section>
        </TabsContent>

        {/* ── Product Page ── */}
        <TabsContent value="product" className="space-y-6 pt-4">
          <Section id="assurance" title="Assurance badges" hint="The trust badges below the product info.">
            <RepeatableRows
              items={draft.assuranceBadges}
              onChange={(v) => set('assuranceBadges', v)}
              addLabel="Add badge"
              makeBlank={() => ({ label: '', icon: 'shield' as const })}
              fields={[
                { kind: 'text', key: 'label', label: 'Label' },
                { kind: 'icon', key: 'icon', label: 'Icon' },
              ]}
              preview={(row) => <IconRowPreview row={row} />}
            />
          </Section>
          <Section id="trust" title="Trust badges (official store)" hint="The trust ribbon shown on the official-store page.">
            <RepeatableRows
              items={draft.trustBadges}
              onChange={(v) => set('trustBadges', v)}
              addLabel="Add badge"
              makeBlank={() => ({ label: '', icon: 'shield' as const })}
              fields={[
                { kind: 'text', key: 'label', label: 'Label' },
                { kind: 'icon', key: 'icon', label: 'Icon' },
              ]}
              preview={(row) => <IconRowPreview row={row} />}
            />
          </Section>
          <Section id="product-vouchers" title="Product voucher badges" hint="Small badges in the product price box.">
            <RepeatableRows
              items={draft.productVoucherBadges}
              onChange={(v) => set('productVoucherBadges', v)}
              addLabel="Add badge"
              makeBlank={() => ({ line1: '', line2: '' })}
              fields={[
                { kind: 'text', key: 'line1', label: 'Line 1' },
                { kind: 'text', key: 'line2', label: 'Line 2' },
              ]}
            />
          </Section>
          <Section id="product-stats" title="Product store-card stats" hint="The seller stats grid below the product.">
            <RepeatableRows
              items={draft.productStoreCard.stats}
              onChange={(v) => set('productStoreCard', { stats: v })}
              addLabel="Add stat"
              makeBlank={() => ({ value: '', label: '' })}
              fields={[
                { kind: 'text', key: 'value', label: 'Value' },
                { kind: 'text', key: 'label', label: 'Label' },
              ]}
            />
          </Section>
        </TabsContent>

        {/* ── Header & Footer ── */}
        <TabsContent value="chrome" className="space-y-6 pt-4">
          <Section id="util-links" title="Header utility links" hint="The small links in the top utility strip.">
            <RepeatableRows
              items={draft.header.utilLinks}
              onChange={(v) => set('header', { ...draft.header, utilLinks: v })}
              addLabel="Add link"
              makeBlank={() => ({ label: '', href: '#' })}
              fields={[
                { kind: 'text', key: 'label', label: 'Label' },
                { kind: 'text', key: 'href', label: 'Link', mono: true },
              ]}
            />
          </Section>
          <Section id="search-suggestions" title="Search suggestions" hint="When set, these override the automatic popular searches; leave empty to rank by recent sales.">
            <StringListEditor
              items={draft.header.searchSuggestions}
              onChange={(v) => set('header', { ...draft.header, searchSuggestions: v })}
              placeholder="Wellness Coffee"
              addLabel="Add suggestion"
            />
          </Section>
          <Section id="footer-brand" title="Footer brand">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="Brand name">
                <Input
                  value={draft.footer.brandName}
                  onChange={(e) => set('footer', { ...draft.footer, brandName: e.target.value })}
                />
              </Labeled>
              <Labeled label="Copyright text">
                <Input
                  value={draft.footer.copyrightText}
                  onChange={(e) => set('footer', { ...draft.footer, copyrightText: e.target.value })}
                />
              </Labeled>
              <Labeled label="Brand blurb">
                <Textarea
                  rows={3}
                  value={draft.footer.brandBlurb}
                  onChange={(e) => set('footer', { ...draft.footer, brandBlurb: e.target.value })}
                />
              </Labeled>
              <Labeled label="Permit / legal line">
                <Input
                  value={draft.footer.permitText}
                  onChange={(e) => set('footer', { ...draft.footer, permitText: e.target.value })}
                />
              </Labeled>
            </div>
          </Section>
          <Section id="footer-columns" title="Footer columns" hint="Each column has a heading and a list of links.">
            <FooterColumnsEditor
              columns={draft.footer.columns}
              onChange={(cols) => set('footer', { ...draft.footer, columns: cols })}
            />
          </Section>
        </TabsContent>

        {/* ── Pages ── */}
        <TabsContent value="pages" className="space-y-6 pt-4">
          <Section id="page-about" title="About Us" hint="Reachable at /about and from the footer.">
            <PageEditor
              page={draft.pages.about}
              onChange={(p) => set('pages', { ...draft.pages, about: p })}
            />
          </Section>
          <Section
            id="page-company"
            title="About — Company (Mission / Vision / Profile / Testimonials)"
            hint="Shown on /about below the About Us content. Empty fields and an empty testimonials list are hidden."
          >
            <CompanyEditor company={draft.company} onChange={(c) => set('company', c)} />
          </Section>
          <Section id="page-become-member" title="Become a Member" hint="Reachable at /become-member.">
            <PageEditor
              page={draft.pages.becomeMember}
              onChange={(p) => set('pages', { ...draft.pages, becomeMember: p })}
            />
          </Section>
          <Section
            id="page-sell"
            title="“Sell on …” page (merchant recruiting)"
            hint="Reachable at /sell-on-omni. The title uses your brand name automatically."
          >
            <PageEditor
              page={draft.pages.sellOnOmni}
              onChange={(p) => set('pages', { ...draft.pages, sellOnOmni: p })}
            />
          </Section>
          <Section
            id="page-merchant"
            title="Become a Merchant (guide)"
            hint="Reachable at /become-merchant — steps, documents needed, and Terms & Agreement."
          >
            <PageEditor
              page={draft.pages.merchantGuide}
              onChange={(p) => set('pages', { ...draft.pages, merchantGuide: p })}
            />
          </Section>
        </TabsContent>

        {/* ── Site Pages — bespoke tenant site text & images (manifest-driven) ── */}
        {SITE_MANIFEST && (
          <TabsContent value="sitePages" className="pt-4">
            <SitePagesEditor
              manifest={SITE_MANIFEST}
              overlay={draft.siteContent}
              onChange={(sc) => set('siteContent', sc)}
              readOnly={!canWrite}
            />
          </TabsContent>
        )}

        {/* ── Help & Social ── */}
        <TabsContent value="help" className="space-y-6 pt-4">
          <Section id="faq" title="Customer Care / FAQ" hint="Reachable at /faq. Empty = page hidden.">
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="Page title">
                <Input
                  value={draft.faq.title}
                  onChange={(e) => set('faq', { ...draft.faq, title: e.target.value })}
                />
              </Labeled>
              <Labeled label="Intro (optional)">
                <Input
                  value={draft.faq.intro ?? ''}
                  onChange={(e) => set('faq', { ...draft.faq, intro: e.target.value })}
                />
              </Labeled>
            </div>
            <RepeatableRows
              items={draft.faq.entries}
              onChange={(v) => set('faq', { ...draft.faq, entries: v })}
              addLabel="Add Q&A"
              columns={1}
              makeBlank={() => ({ question: '', answer: '', category: '' })}
              fields={[
                { kind: 'text', key: 'question', label: 'Question' },
                { kind: 'textarea', key: 'answer', label: 'Answer' },
                { kind: 'text', key: 'category', label: 'Category (optional, groups entries)' },
              ]}
            />
          </Section>
          <Section
            id="social"
            title="Follow Us"
            hint="Only entries with a link are shown. Upload a logo per platform."
          >
            <RepeatableRows
              items={draft.social}
              onChange={(v) => set('social', v)}
              addLabel="Add social link"
              makeBlank={() => ({ platform: '', url: '', logoKey: '' })}
              fields={[
                { kind: 'text', key: 'platform', label: 'Platform (e.g. Facebook)' },
                { kind: 'text', key: 'url', label: 'Link', mono: true },
                { kind: 'image', key: 'logoKey', label: 'Logo (optional)' },
              ]}
            />
          </Section>
          <Section id="payment" title="Payment page copy" hint="Reachable at /payment.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="Page title">
                <Input
                  value={draft.payment.title}
                  onChange={(e) => set('payment', { ...draft.payment, title: e.target.value })}
                />
              </Labeled>
              <Labeled label="Intro (optional)">
                <Input
                  value={draft.payment.intro ?? ''}
                  onChange={(e) => set('payment', { ...draft.payment, intro: e.target.value })}
                />
              </Labeled>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              The payment options shown on this page are pulled live from the enabled methods in
              Finance → Payment Methods. Edit the methods there; this controls only the heading and
              intro text.
            </p>
          </Section>
        </TabsContent>

        {/* ── General ── */}
        <TabsContent value="general" className="space-y-6 pt-4">
          <Section id="copy" title="Copy strings">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Labeled label="Shipping guarantee">
                <Input
                  value={draft.copy.shippingGuarantee}
                  onChange={(e) => set('copy', { ...draft.copy, shippingGuarantee: e.target.value })}
                />
              </Labeled>
            </div>
          </Section>
          <Section id="display" title="Display options">
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.showDecorativeSold}
                onCheckedChange={(v) => set('showDecorativeSold', v === true)}
              />
              <span className="text-muted-foreground">
                Show decorative star ratings on product cards
              </span>
            </label>
          </Section>
        </TabsContent>
            </Tabs>
          </div>
          <aside className="sticky top-4 hidden w-[34%] shrink-0 lg:block">
            <StorefrontMap
              tab={tab}
              active={activeSection}
              onPick={(id) => {
                setActiveSection(id);
                document
                  .querySelector(`[data-section="${id}"]`)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            />
          </aside>
        </div>
      </SetActiveSectionContext.Provider>
    </div>
  );
}

/** Lets each Section register itself as "active" (hovered/focused) so the
 * locator pane can highlight where it lands on the storefront page. */
const SetActiveSectionContext = createContext<(id: string | null) => void>(() => {});

function Section({
  id,
  title,
  hint,
  children,
}: {
  id?: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const setActive = useContext(SetActiveSectionContext);
  return (
    <Card
      data-section={id}
      onMouseEnter={() => id && setActive(id)}
      onFocusCapture={() => id && setActive(id)}
    >
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * Product picker for the official-store "Member-Exclusive Deals" row. Selection
 * order = display order. House products are the storefront's official catalog;
 * the picker lists all products and the storefront resolves ids against the
 * loaded house catalog (so a non-house id simply won't render).
 */
function MemberExclusiveDealsEditor({
  selectedIds,
  onChange,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const productsQ = useQuery({
    queryKey: ['admin-products', 'member-deals-pick'],
    queryFn: () => listProducts({ pageSize: 200 }),
  });
  const [search, setSearch] = useState('');
  const products = productsQ.data ?? [];
  const byId = new Map(products.map((p) => [p.id, p]));
  const selected = selectedIds
    .map((id) => byId.get(id))
    .filter((p): p is Product => Boolean(p));
  const term = search.trim().toLowerCase();
  const filtered = products.filter(
    (p) =>
      !selectedIds.includes(p.id) &&
      (term === '' || p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term)),
  );

  const add = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds : [...selectedIds, id]);
  const removeId = (id: string) => onChange(selectedIds.filter((x) => x !== id));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Selected (ordered) */}
      <div>
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Selected ({selected.length})
        </p>
        {selected.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No products selected — the row is hidden. Add some from the list →
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {selected.map((p, i) => (
              <li key={p.id} className="flex items-center gap-2 px-3 py-2">
                <span className="w-5 text-xs text-muted-foreground">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{p.sku}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => move(i, 1)}
                  disabled={i === selected.length - 1}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => removeId(p.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Picker */}
      <div className="space-y-3">
        <Input
          placeholder="Search by name or SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {productsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {products.length === 0 ? 'No products yet — add products first.' : 'No matches.'}
          </p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
            {filtered.map((p) => (
              <li key={p.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{p.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {p.sku}
                    {!p.active && ' · inactive'}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => add(p.id)}>
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

type RegionKind = 'hero' | 'banners' | 'statbar' | 'grid' | 'row' | 'bar' | 'footer' | 'note';
type MapRegion = { id: string; label: string; kind: RegionKind };

/** Storefront regions per editor tab, in rough top-to-bottom page order. The
 * `id` matches each Section's `id` so hovering a section highlights its region. */
const TAB_REGIONS: Record<string, MapRegion[]> = {
  brand: [
    { id: 'brand-identity', label: 'Brand identity', kind: 'note' },
    { id: 'brand-terms', label: 'Storefront terms', kind: 'note' },
    { id: 'brand-logos', label: 'Logos', kind: 'footer' },
  ],
  home: [
    { id: 'hero', label: 'Hero slides', kind: 'hero' },
    { id: 'home-side-banners', label: 'Hero side banners', kind: 'banners' },
    { id: 'home-banners', label: 'Bottom banners', kind: 'banners' },
  ],
  official: [
    { id: 'store-stats', label: 'Store stats', kind: 'statbar' },
    { id: 'collections', label: 'Collections', kind: 'grid' },
    { id: 'campaign', label: 'Campaign banners', kind: 'banners' },
  ],
  shop: [
    { id: 'shop-stats', label: 'Shop header stats', kind: 'statbar' },
    { id: 'vouchers', label: 'Vouchers', kind: 'row' },
  ],
  product: [
    { id: 'product-vouchers', label: 'Product voucher badges', kind: 'row' },
    { id: 'assurance', label: 'Assurance badges', kind: 'row' },
    { id: 'product-stats', label: 'Store-card stats', kind: 'grid' },
    { id: 'trust', label: 'Trust badges (official store)', kind: 'row' },
  ],
  chrome: [
    { id: 'util-links', label: 'Header utility links', kind: 'bar' },
    { id: 'search-suggestions', label: 'Search suggestions', kind: 'row' },
    { id: 'footer-brand', label: 'Footer brand', kind: 'footer' },
    { id: 'footer-columns', label: 'Footer columns', kind: 'footer' },
  ],
  pages: [
    { id: 'page-about', label: 'About Us', kind: 'note' },
    { id: 'page-company', label: 'Company section', kind: 'note' },
    { id: 'page-become-member', label: 'Become a Member', kind: 'note' },
    { id: 'page-sell', label: '“Sell on …” page', kind: 'note' },
    { id: 'page-merchant', label: 'Become a Merchant', kind: 'note' },
  ],
  // The bespoke Site Pages tab renders the tenant's own pages, not the shared
  // storefront — the locator pane has nothing to point at.
  sitePages: [],
  help: [
    { id: 'faq', label: 'Customer Care / FAQ', kind: 'note' },
    { id: 'social', label: 'Follow Us', kind: 'footer' },
    { id: 'payment', label: 'Payment page', kind: 'note' },
  ],
  general: [
    { id: 'copy', label: 'Copy strings', kind: 'note' },
    { id: 'display', label: 'Display options', kind: 'note' },
  ],
};

/** The wireframe glyph for a region, dimmed normally and tinted when active. */
function RegionGlyph({ kind, active }: { kind: RegionKind; active: boolean }) {
  const box = active ? 'bg-primary/40' : 'bg-muted';
  switch (kind) {
    case 'hero':
      return <div className={`h-14 rounded ${box}`} />;
    case 'banners':
      return (
        <div className="grid grid-cols-2 gap-1">
          <div className={`h-8 rounded ${box}`} />
          <div className={`h-8 rounded ${box}`} />
        </div>
      );
    case 'statbar':
      return (
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-5 flex-1 rounded ${box}`} />
          ))}
        </div>
      );
    case 'grid':
      return (
        <div className="grid grid-cols-3 gap-1">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`h-6 rounded ${box}`} />
          ))}
        </div>
      );
    case 'row':
      return (
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-5 flex-1 rounded-full ${box}`} />
          ))}
        </div>
      );
    case 'bar':
      return <div className={`h-3 rounded ${box}`} />;
    case 'footer':
      return <div className={`h-10 rounded ${box}`} />;
    case 'note':
      return (
        <div className="space-y-1">
          <div className={`h-2 w-3/4 rounded ${box}`} />
          <div className={`h-2 w-1/2 rounded ${box}`} />
        </div>
      );
  }
}

/** "Where it appears" locator: a labeled wireframe of the active tab's
 * storefront page. The region matching the hovered/edited section lights up,
 * and clicking a region scrolls its editor section into view. Self-contained —
 * no live storefront connection needed, just a spatial map. */
function StorefrontMap({
  tab,
  active,
  onPick,
}: {
  tab: string;
  active: string | null;
  onPick: (id: string) => void;
}) {
  const regions = TAB_REGIONS[tab] ?? [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Where it appears</CardTitle>
        <p className="text-xs text-muted-foreground">
          Hover a section on the left to locate it on the page; click a block to jump to it.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border bg-background shadow-sm">
          {/* faux browser chrome */}
          <div className="flex items-center gap-1 border-b bg-muted/60 px-2 py-1.5">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
            <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
            <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
          </div>
          {/* faux store header for page orientation */}
          {tab !== 'general' && (
            <div className="flex items-center gap-2 border-b px-2 py-2">
              <div className="h-4 w-10 rounded bg-primary/70" />
              <div className="h-5 flex-1 rounded bg-muted" />
            </div>
          )}
          <div className="space-y-2 p-2">
            {regions.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                Applies across the storefront.
              </p>
            ) : (
              regions.map((r) => {
                const on = active === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onPick(r.id)}
                    className={`block w-full rounded-md p-2 text-left transition ${
                      on ? 'bg-primary/5 ring-2 ring-primary' : 'ring-1 ring-border hover:ring-primary/40'
                    }`}
                  >
                    <div
                      className={`mb-1 text-[10px] font-medium uppercase tracking-wide ${
                        on ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      {r.label}
                    </div>
                    <RegionGlyph kind={r.kind} active={on} />
                  </button>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/** A simple add/remove/reorder editor over a list of plain strings. */
function StringListEditor({
  items,
  onChange,
  placeholder,
  addLabel,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  addLabel: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            className="h-8"
            value={s}
            placeholder={placeholder}
            onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))}
          />
          <button
            type="button"
            aria-label="Remove"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...items, ''])}>
        <Plus className="h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
}

/** Footer columns: each column is a heading + a list of {label, href} links. */
function FooterColumnsEditor({
  columns,
  onChange,
}: {
  columns: StorefrontConfig['footer']['columns'];
  onChange: (cols: StorefrontConfig['footer']['columns']) => void;
}) {
  const linkFields: RowField[] = [
    { kind: 'text', key: 'label', label: 'Label' },
    { kind: 'text', key: 'href', label: 'Link', mono: true },
  ];
  return (
    <div className="space-y-4">
      {columns.map((col, i) => (
        <Card key={i}>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <Input
              className="h-8 max-w-xs"
              value={col.heading}
              placeholder="Column heading"
              onChange={(e) =>
                onChange(columns.map((c, idx) => (idx === i ? { ...c, heading: e.target.value } : c)))
              }
            />
            <Button
              variant="ghost"
              size="sm"
              aria-label="Remove column"
              onClick={() => onChange(columns.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <RepeatableRows
              items={col.links}
              fields={linkFields}
              makeBlank={() => ({ label: '', href: '#' })}
              addLabel="Add link"
              onChange={(links) =>
                onChange(columns.map((c, idx) => (idx === i ? { ...c, links } : c)))
              }
            />
          </CardContent>
        </Card>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...columns, { heading: '', links: [] }])}
      >
        <Plus className="h-4 w-4" /> Add column
      </Button>
    </div>
  );
}

/** Editor for the About-page company section: mission / vision / profile text
 * plus a typed testimonials list (name / role / quote / photo). */
function CompanyEditor({
  company,
  onChange,
}: {
  company: StorefrontConfig['company'];
  onChange: (c: StorefrontConfig['company']) => void;
}) {
  const set = <K extends keyof StorefrontConfig['company']>(
    key: K,
    value: StorefrontConfig['company'][K],
  ) => onChange({ ...company, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labeled label="Mission">
          <Textarea rows={3} value={company.mission} onChange={(e) => set('mission', e.target.value)} />
        </Labeled>
        <Labeled label="Vision">
          <Textarea rows={3} value={company.vision} onChange={(e) => set('vision', e.target.value)} />
        </Labeled>
      </div>
      <Labeled label="Company profile (blank lines separate paragraphs)">
        <Textarea rows={5} value={company.profile} onChange={(e) => set('profile', e.target.value)} />
      </Labeled>
      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Testimonials</p>
        <RepeatableRows
          items={company.testimonials}
          onChange={(v) => set('testimonials', v)}
          addLabel="Add testimonial"
          columns={1}
          makeBlank={() => ({ author: '', role: '', quote: '', avatarKey: '' })}
          fields={[
            { kind: 'text', key: 'author', label: 'Name' },
            { kind: 'text', key: 'role', label: 'Role / location (optional)' },
            { kind: 'textarea', key: 'quote', label: 'Testimonial' },
            { kind: 'image', key: 'avatarKey', label: 'Photo (optional)' },
          ]}
        />
      </div>
    </div>
  );
}

/** Editor for one informational page (title/intro/hero + sections + CTA). */
function PageEditor({ page, onChange }: { page: StorefrontPage; onChange: (p: StorefrontPage) => void }) {
  const set = <K extends keyof StorefrontPage>(key: K, value: StorefrontPage[K]) =>
    onChange({ ...page, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labeled label="Title">
          <Input value={page.title} onChange={(e) => set('title', e.target.value)} />
        </Labeled>
        <Labeled label="Intro (optional)">
          <Input value={page.intro ?? ''} onChange={(e) => set('intro', e.target.value || undefined)} />
        </Labeled>
      </div>
      <Labeled label="Hero image (optional)">
        <PageHeroImage value={page.heroImageKey ?? ''} onChange={(k) => set('heroImageKey', k || undefined)} />
      </Labeled>
      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Sections</p>
        <PageSectionsEditor sections={page.sections} onChange={(s) => set('sections', s)} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labeled label="Call-to-action label (optional)">
          <Input value={page.ctaLabel ?? ''} onChange={(e) => set('ctaLabel', e.target.value || undefined)} />
        </Labeled>
        <Labeled label="Call-to-action link (optional)">
          <Input
            className="font-mono"
            value={page.ctaHref ?? ''}
            onChange={(e) => set('ctaHref', e.target.value || undefined)}
          />
        </Labeled>
      </div>
    </div>
  );
}

const SECTION_KINDS: { value: StorefrontPageSection['kind']; label: string }[] = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'steps', label: 'Steps (numbered list)' },
  { value: 'docs', label: 'Bullet list' },
];

function blankSection(kind: StorefrontPageSection['kind']): StorefrontPageSection {
  if (kind === 'steps') return { kind: 'steps', heading: '', steps: [''] };
  if (kind === 'docs') return { kind: 'docs', heading: '', items: [''] };
  return { kind: 'paragraph', heading: '', body: '' };
}

/** Add/remove/reorder editor over a page's ordered sections. Each section picks
 * a kind, then edits a paragraph body or a string list (steps / docs). */
function PageSectionsEditor({
  sections,
  onChange,
}: {
  sections: StorefrontPageSection[];
  onChange: (s: StorefrontPageSection[]) => void;
}) {
  const replace = (i: number, next: StorefrontPageSection) =>
    onChange(sections.map((s, idx) => (idx === i ? next : s)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {sections.map((s, i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-3 py-3">
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Labeled label="Section type">
                  <Select
                    value={s.kind}
                    onValueChange={(k) =>
                      replace(i, { ...blankSection(k as StorefrontPageSection['kind']), heading: s.heading })
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTION_KINDS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Labeled>
                <Labeled label="Heading (optional)">
                  <Input
                    className="h-8"
                    value={s.heading ?? ''}
                    onChange={(e) => replace(i, { ...s, heading: e.target.value })}
                  />
                </Labeled>
              </div>
              {s.kind === 'paragraph' && (
                <Labeled label="Body">
                  <Textarea
                    rows={4}
                    value={s.body}
                    onChange={(e) => replace(i, { ...s, body: e.target.value })}
                  />
                </Labeled>
              )}
              {s.kind === 'steps' && (
                <StringListEditor
                  items={s.steps}
                  onChange={(v) => replace(i, { ...s, steps: v })}
                  placeholder="Step description"
                  addLabel="Add step"
                />
              )}
              {s.kind === 'docs' && (
                <StringListEditor
                  items={s.items}
                  onChange={(v) => replace(i, { ...s, items: v })}
                  placeholder="List item"
                  addLabel="Add item"
                />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                aria-label="Move up"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Move down"
                disabled={i === sections.length - 1}
                onClick={() => move(i, 1)}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Remove section"
                onClick={() => onChange(sections.filter((_, idx) => idx !== i))}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      ))}
      <div className="flex flex-wrap gap-2">
        {SECTION_KINDS.map((o) => (
          <Button
            key={o.value}
            variant="outline"
            size="sm"
            onClick={() => onChange([...sections, blankSection(o.value)])}
          >
            <Plus className="h-4 w-4" /> {o.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** A single optional page hero image: upload → store the R2 key, preview via the
 * public read. Mirrors the row `ImageField` but for a standalone (non-row) field. */
function PageHeroImage({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const src = value ? storefrontAssetUrl(value) : null;
  return (
    <div className="flex items-center gap-3">
      {src ? (
        <img src={src} alt="" className="h-12 w-20 rounded border bg-white object-cover" />
      ) : (
        <div className="h-12 w-20 rounded border bg-muted" />
      )}
      <input
        type="file"
        accept="image/*"
        className="text-xs"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setUploading(true);
          uploadStorefrontAsset(f)
            .then(({ key }) => onChange(key))
            .finally(() => setUploading(false));
        }}
      />
      {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
      {value && !uploading && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-destructive"
          onClick={() => onChange('')}
        >
          Remove
        </button>
      )}
    </div>
  );
}
